/**
 * ░██████╗░██╗████████╗██╗░░██╗██╗░░░██╗██████╗░  ██╗
 * ██╔════╝░██║╚══██╔══╝██║░░██║██║░░░██║██╔══██╗  ╚═╝
 * ██║░░██╗░██║░░░██║░░░███████║██║░░░██║██████╦╝  ░░░
 * ██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░██║██╔══██╗  ░░░
 * ╚██████╔╝██║░░░██║░░░██║░░██║╚██████╔╝██████╦╝  ██╗
 * ░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝░╚═════╝░╚═════╝░  ╚═╝
 * 
 * ███╗░░██╗░█████╗░████████╗██╗░░██╗░█████╗░███╗░░██╗███████╗██╗░░██╗░█████╗░
 * ████╗░██║██╔══██╗╚══██╔══╝██║░██╔╝██╔══██╗████╗░██║██╔════╝██║░██╔╝██╔══██╗
 * ██╔██╗██║██║░░██║░░░██║░░░█████═╝░██║░░██║██╔██╗██║█████╗░░█████═╝░██║░░██║
 * ██║╚████║██║░░██║░░░██║░░░██╔═██╗░██║░░██║██║╚████║██╔══╝░░██╔═██╗░██║░░██║
 * ██║░╚███║╚█████╔╝░░░██║░░░██║░╚██╗╚█████╔╝██║░╚███║███████╗██║░╚██╗╚█████╔╝
 * ╚═╝░░╚══╝░╚════╝░░░░╚═╝░░░╚═╝░░╚═╝░╚════╝░╚═╝░░╚══╝╚══════╝╚═╝░░╚═╝░╚════╝░
 */

import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Starfield from './components/Starfield';
import HomePage from './pages/HomePage';
import Projects from './pages/Projects';
import Ideas from './pages/Ideas';
import About from './pages/About';
import ProjectDetail from './pages/ProjectDetail';
import DiscordBotDetail from './pages/DiscordBotDetail';
import LyrikaTosPage from './pages/LyrikaTosPage';
import LyrikaPrivacyPage from './pages/LyrikaPrivacyPage';
import LyrikaStanzaPage from './pages/LyrikaStanzaPage';
import IdeaDetail from './pages/IdeaDetail';
import NotFound from './pages/NotFound';

// Only renders Starfield background on the Home Page ('/')
const GlobalBackground = ({ isLyrikaSubdomain }) => {
  const location = useLocation();

  // If on lyrika subdomain or on any page other than home, do not render Starfield background
  if (isLyrikaSubdomain || location.pathname !== '/') {
    return null;
  }

  return <Starfield />;
};

function MainLayout() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <main className={`${isHome ? 'w-full pt-0 pb-12' : 'max-w-6xl mx-auto px-4 sm:px-6 pt-3 pb-6 sm:pt-6 sm:pb-12'} relative z-10`}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/ideas" element={<Ideas />} />
        <Route path="/about" element={<About />} />

        {/* Standalone Legal Routes for Discord App Verification */}
        <Route path="/projects/discordbots/lyrika-stanza/tos" element={<LyrikaTosPage />} />
        <Route path="/projects/discordbots/lyrika-stanza/privacy" element={<LyrikaPrivacyPage />} />
        <Route path="/lyrika-stanza/tos" element={<LyrikaTosPage />} />
        <Route path="/lyrika-stanza/privacy" element={<LyrikaPrivacyPage />} />
        <Route path="/lyrika/tos" element={<LyrikaTosPage />} />
        <Route path="/lyrika/privacy" element={<LyrikaPrivacyPage />} />

        <Route path="/projects/discordbots/:botId" element={<DiscordBotDetail />} />
        <Route path="/projects/:id" element={<ProjectDetail />} /> 
        <Route path="/ideas/:id" element={<IdeaDetail />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
  );
}

function App() {
  const isLyrikaSubdomain = typeof window !== 'undefined' && 
    window.location.hostname.toLowerCase().includes('lyrika');

  // If accessed via lyrika.* subdomain, isolate the site to ONLY the Lyrika Stanza page & legal pages
  if (isLyrikaSubdomain) {
    return (
      <Router>
        <ScrollToTop />
        <div className="bg-[#050409] text-gray-200 min-h-screen font-sans selection:bg-fuchsia-500/30 selection:text-fuchsia-200 relative">
          <GlobalBackground isLyrikaSubdomain={true} />
          <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-3 pb-6 sm:pt-6 sm:pb-12 relative z-10">
            <Routes>
              <Route path="/tos" element={<LyrikaTosPage />} />
              <Route path="/privacy" element={<LyrikaPrivacyPage />} />
              <Route path="*" element={<LyrikaStanzaPage />} />
            </Routes>
          </main>
        </div>
      </Router>
    );
  }

  // Default main portfolio routing for portfolio.notkoneko.space
  return (
    <Router>
      <ScrollToTop />
      <div className="bg-[#050409] text-gray-200 min-h-screen font-sans selection:bg-fuchsia-500/30 selection:text-fuchsia-200 relative">
        <GlobalBackground isLyrikaSubdomain={false} />
        <Navbar />
        <MainLayout />
      </div>
    </Router>
  );
}

export default App;